'use client';

import LoadingSpinner from '@umodeapporg/ui/components/UI/LoadingSpinner';
import NoDataFound from '@umodeapporg/ui/components/UI/NoDataFound';
import { RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { IConversaView } from '@/domain/agente/interfaces/agente';
import { cn } from '@/lib/utils';

import { AgenteAtalhos } from './AgenteAtalhos';
import { MensagemBubble } from './MensagemBubble';
import { PropostaCard } from './PropostaCard';

interface IAgenteConversaProps {
  conversa: IConversaView;
  loading: boolean;
  loadingConfirmar: string | null;
  onConfirmar: (id: string) => Promise<void>;
  onAtalho: (texto: string) => Promise<void>;
  /** Called when a shortcut leaves the agent for a screen (the side panel closes itself). */
  onNavegar?: () => void;
  /** Anchor the host scrolls into view after sending. */
  bottomRef: RefObject<HTMLDivElement | null>;
  /** `md` on the full page, `sm` inside the side panel. */
  size?: 'sm' | 'md';
  className?: string;
}

/** The scrollable thread: empty state with shortcuts, message bubbles, pending proposals and the thinking bubble. */
export function AgenteConversa({
  conversa,
  loading,
  loadingConfirmar,
  onConfirmar,
  onAtalho,
  onNavegar,
  bottomRef,
  size = 'md',
  className,
}: IAgenteConversaProps) {
  const { t } = useTranslation('common');

  const vazia = conversa.mensagens.length === 0 && !loading;

  return (
    <div className={cn('flex flex-col gap-4 overflow-y-auto', className)}>
      {vazia && (
        <div className="flex flex-col items-center gap-4">
          <NoDataFound variant="empty" size={size} icon="smart_toy" title={t('agente.semMensagens')} />
          <div className="w-full max-w-sm">
            <AgenteAtalhos onAtalho={onAtalho} onNavegar={onNavegar} disabled={loading} />
          </div>
        </div>
      )}

      {conversa.mensagens.map((mensagem, index) => (
        <MensagemBubble key={`${mensagem.quando}-${index}`} papel={mensagem.papel} texto={mensagem.texto} />
      ))}

      {conversa.propostasPendentes.map((proposta) => (
        <PropostaCard key={proposta.id} proposta={proposta} onConfirmar={onConfirmar} loadingId={loadingConfirmar} />
      ))}

      {loading && (
        <div className="flex justify-start">
          <div className="flex items-center gap-2 rounded-2xl rounded-bl-sm bg-muted px-4 py-2.5 text-sm text-foreground-muted">
            <LoadingSpinner size={4} color="foreground-faint" />
            {t('agente.loading')}
          </div>
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
}
